import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Zap, CalendarClock, Calendar, DollarSign } from "lucide-react";
import { fmtMoney } from "@/lib/format";

const todayIso = () => new Date().toISOString().slice(0, 10);
const endOfQuarter = () => {
    // The quarterly cycles anchor on the calendar quarter: Jan 1,
    // Apr 1, Jul 1, Oct 1. Prorate to the LAST day of the current quarter.
    const d = new Date();
    const m = d.getMonth();
    const nextQm = (Math.floor(m / 3) + 1) * 3;   // 3, 6, 9, 12
    const end = new Date(d.getFullYear(), nextQm, 0);  // last day of quarter
    return end.toISOString().slice(0, 10);
};

/** First Invoice Wizard — three modes:
 *   * bill_now — send full first invoice today
 *   * prorate  — prorate to a chosen anchor date, then bill quarterly from there
 *   * future   — schedule the first invoice to auto-generate on the anchor date
 */
export function FirstInvoiceDialog({ open, onOpenChange, customer, onDone }) {
    const [mode, setMode] = useState("bill_now");
    const [anchor, setAnchor] = useState(endOfQuarter());
    const [busy, setBusy] = useState(false);

    if (!customer) return null;

    const rate = parseFloat(customer.monthly_rate) || 0;
    const days = mode === "prorate"
        ? Math.max(1, Math.min(90,
            Math.ceil((new Date(anchor + "T00:00:00") - new Date()) / (86400000)),
        ))
        : 0;
    const proratedAmount = mode === "prorate"
        ? Math.round(rate * (days / 90) * 100) / 100
        : rate;

    const start = async () => {
        if (mode === "prorate" && (!anchor || anchor <= todayIso())) {
            return toast.error("Prorate anchor date must be in the future");
        }
        if (mode === "future" && (!anchor || anchor <= todayIso())) {
            return toast.error("Future date must be after today");
        }
        setBusy(true);
        try {
            const payload = { mode };
            if (mode !== "bill_now") payload.anchor_date = anchor;
            const { data } = await api.post(
                `/customers/${customer.id}/start-billing`, payload,
            );
            if (mode === "future") {
                toast.success(`First invoice scheduled for ${anchor}`);
            } else if (mode === "prorate") {
                toast.success(`Prorated invoice sent — ${fmtMoney(data.amount)}`);
            } else {
                toast.success(`First invoice sent — ${fmtMoney(data.amount)}`);
            }
            onDone?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start billing");
        } finally { setBusy(false); }
    };

    const modes = [
        {
            id: "bill_now", icon: Zap, title: "Bill now",
            desc: `Send the full ${fmtMoney(rate)} first invoice today. Next auto-bill in 3 months.`,
        },
        {
            id: "prorate", icon: DollarSign, title: "Prorate to a date",
            desc: `Send a partial invoice today covering today → your chosen date. Quarterly cycle starts from that date.`,
        },
        {
            id: "future", icon: Calendar, title: "Bill on future date",
            desc: `Don't send anything now — schedule the first invoice to auto-generate on your chosen date.`,
        },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="first-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5" />
                        Start recurring billing
                    </DialogTitle>
                    <DialogDescription>
                        {customer.name} · quarterly rate {fmtMoney(rate)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    {modes.map((m) => {
                        const Icon = m.icon;
                        const active = mode === m.id;
                        return (
                            <button
                                key={m.id}
                                onClick={() => setMode(m.id)}
                                className={
                                    "w-full text-left px-3 py-3 rounded-md border flex items-start gap-3 transition " +
                                    (active
                                        ? "border-primary bg-primary/5"
                                        : "border-border hover:bg-secondary/40")
                                }
                                data-testid={`first-invoice-mode-${m.id}`}
                            >
                                <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + (active ? "text-primary" : "text-muted-foreground")} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">{m.title}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {(mode === "prorate" || mode === "future") && (
                    <div>
                        <Label className="text-xs mb-1 block">
                            {mode === "prorate" ? "Prorate through (end of period)" : "First bill date"}
                        </Label>
                        <Input
                            type="date"
                            min={todayIso()}
                            value={anchor}
                            onChange={(e) => setAnchor(e.target.value)}
                            data-testid="first-invoice-anchor-date"
                        />
                        {mode === "prorate" && (
                            <div className="text-xs text-muted-foreground mt-1.5">
                                Sends <strong className="text-foreground">{fmtMoney(proratedAmount)}</strong> today for {days} of 90 days.
                                Next full invoice ~3 months after {anchor}.
                            </div>
                        )}
                    </div>
                )}

                <div className="flex gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                            data-testid="first-invoice-cancel-btn">
                        Cancel
                    </Button>
                    <Button onClick={start} disabled={busy || rate <= 0}
                            data-testid="first-invoice-start-btn">
                        {busy ? "Starting…" :
                            mode === "future" ? "Schedule" :
                            mode === "prorate" ? `Send ${fmtMoney(proratedAmount)}` :
                            `Send ${fmtMoney(rate)} now`}
                    </Button>
                </div>
                {rate <= 0 && (
                    <div className="text-xs text-red-600">
                        Set a rate on this customer's edit form before starting billing.
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
